import React from "react";
import { motion } from "framer-motion";
import { Code2, Rocket, Sparkles, Palette, Bot, LineChart } from "lucide-react";

const services = [
  {
    icon: Rocket,
    title: "SaaS MVPs",
    desc: "Concept to live product in 2–3 weeks. Auth, payments, dashboards — the works.",
    bg: "#FFD800",
    span: "md:col-span-2 md:row-span-2",
  },
  {
    icon: Code2,
    title: "Web Apps",
    desc: "Bespoke React + FastAPI builds with the muscle for real product.",
    bg: "#FFFFFF",
    span: "md:col-span-1",
  },
  {
    icon: Bot,
    title: "AI Integrations",
    desc: "GPT, Claude, Gemini, RAG, agents — wired into your actual workflow.",
    bg: "#00E5FF",
    span: "md:col-span-1",
  },
  {
    icon: Palette,
    title: "Landing Pages",
    desc: "High-conversion launch pages that don't look like every other template.",
    bg: "#FF0055",
    text: "white",
    span: "md:col-span-2",
  },
  {
    icon: Sparkles,
    title: "Brand & UI Systems",
    desc: "A visual language you can hand to your team and still recognise next year.",
    bg: "#FFFFFF",
    span: "md:col-span-1",
  },
  {
    icon: LineChart,
    title: "Growth Tooling",
    desc: "Internal dashboards, automations and analytics that actually get used.",
    bg: "#FDFBF7",
    span: "md:col-span-2",
  },
];

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 sm:py-32 px-4 sm:px-6"
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <span
              className="font-mono text-xs uppercase tracking-[0.3em] text-[#4A4A4A]"
              data-testid="services-eyebrow"
            >
              [01] — What I build
            </span>
            <h2 className="mt-3 font-display font-black uppercase text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tighter">
              Services that <br />
              <span className="relative inline-block">
                <span className="relative z-10">ship</span>
                <span className="absolute inset-x-0 bottom-2 h-3 bg-[#FFD800] -z-0" />
              </span>{" "}
              fast.
            </h2>
          </div>
          <p className="max-w-md font-body text-[#4A4A4A]">
            Pick one. Pick a stack of them. Each service is sprint-priced and ships
            with a fixed delivery date — no agency drag.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 md:grid-rows-3 gap-5 sm:gap-6">
          {services.map((s, idx) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: idx * 0.06 }}
                data-testid={`service-card-${idx}`}
                className={`brut-border bg-white brut-shadow brut-lift p-6 sm:p-7 flex flex-col gap-4 ${s.span} ${
                  idx % 2 === 0 ? "" : ""
                }`}
                style={{ background: s.bg, color: s.text || "#0D0D0D" }}
              >
                <div
                  className="w-12 h-12 brut-border flex items-center justify-center"
                  style={{ background: s.text === "white" ? "#0D0D0D" : "#FDFBF7" }}
                >
                  <Icon size={22} color={s.text === "white" ? "#FFD800" : "#0D0D0D"} />
                </div>
                <h3 className="font-display font-black uppercase text-2xl sm:text-3xl leading-tight tracking-tight">
                  {s.title}
                </h3>
                <p className={`font-body text-[15px] leading-relaxed ${s.text === "white" ? "text-white/85" : "text-[#0D0D0D]/80"}`}>
                  {s.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
